import { useReadPresetApi } from '@aibees/shared/api';
import {
  Grid2,
  Icon,
  LoadingScreen,
  usePopover,
} from '@aibees/shared/components';
import { useClearTransferList } from '@aibees/shared/features/transfer-list/hooks/use-clear-transfer-list';
import { useCompareTransferListChanges } from '@aibees/shared/features/transfer-list/hooks/use-compare-transfer-list-changes';
import { useIsClear } from '@aibees/shared/features/transfer-list/hooks/use-is-clear';
import { useIsResetToDefault } from '@aibees/shared/features/transfer-list/hooks/use-is-reset-to-default';
import { useLoadTransferListPreset } from '@aibees/shared/features/transfer-list/hooks/use-load-transfer-list-preset';
import { useResetTransferList } from '@aibees/shared/features/transfer-list/hooks/use-reset-transfer-list';
import { useDebounceFn, useMount } from '@aibees/shared/hooks';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Tooltip } from '@mui/material';
import { memo, useEffect } from 'react';
import { useTableLayoutTemplateContext } from '../advance-table/components/table-layout-modifier-dialog/context';
import { OperationMode } from '../template-management-dialog';
import { TransferListExcluded } from './components/transfer-list-excluded';
import { TransferListIncluded } from './components/transfer-list-included';
import { TransferListOpening } from './components/transfer-list-opening';
import useTransferListStore from './store/transfer-list-store';
import * as Styled from './styles';
import * as SX from './sx';
import type {
  TransferListProps as Props,
  TransferListPresetValue,
} from './types';

export const TransferList = memo(
  ({
    id,
    config,
    element,
    includedTitle,
    excludedTitle,
    tooltipTitle,
    title,
    icon,
    saveButtonProps,
    onSubmit,
    dialogMode,
    state,
    onClose,
    loading,
    requireAtLeastOne,
    requireAtLeastOneText,
  }: Props) => {
    const popoverState = usePopover();
    const store = useTransferListStore();
    const reset = useResetTransferList();
    const clear = useClearTransferList();
    const { setTemplateName, setOperationMode, operationMode } =
      useTableLayoutTemplateContext();
    useLoadTransferListPreset(id);
    const hasChanges = useCompareTransferListChanges(id);
    const isResetToDefault = useIsResetToDefault();
    const isClear = useIsClear();
    const { data } = useReadPresetApi<TransferListPresetValue>(id!, {
      enabled: !!id,
    });

    const applyDataToStore = useDebounceFn(() => {
      if (data) {
        store.setIncluded(data.value.included);
        store.setExcluded(data.value.excluded);
      }
    }, 1000);

    const handleClose = () => {
      applyDataToStore();
      onClose?.();
      popoverState.onClose();
      state?.onClose();
      setOperationMode(OperationMode.CREATE);
      setTemplateName('');
    };

    const handleSubmit = () => {
      if (requireAtLeastOne && store.included?.length === 0) return;
      onSubmit?.(store.included, store.excluded);
      popoverState.onClose();
    };

    const handleReset = () => {
      reset();
    };

    const handleClear = () => {
      clear();
    };

    useMount(() => {
      store.setConfig(config);
    });

    useEffect(() => {
      if (!id) {
        const excludedDraft = config?.items
          ?.filter((x) => x.excluded)
          .map((x) => x.id);
        store.setExcluded(excludedDraft);
      }
    }, [config]);

    return (
      <TransferListOpening
        state={state || popoverState}
        element={element}
        title={title}
        icon={icon}
        onClose={handleClose}
        dialogMode={dialogMode}
        actionElement={
          <Box sx={SX.footer}>
            <Box sx={SX.leftAction}>
              <Button
                startIcon={<Icon name="restore-outline" />}
                variant="outlined"
                disabled={isResetToDefault}
                onClick={handleReset}
                size="small"
              >
                Reset to default
              </Button>
              <Button
                startIcon={<Icon name="clear-formatting" />}
                variant="outlined"
                onClick={handleClear}
                disabled={isClear}
                size="small"
              >
                Clear visible columns
              </Button>
            </Box>

            <Box sx={SX.rightAction}>
              <Button
                size="small"
                variant="text"
                color="default"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Tooltip
                title={
                  requireAtLeastOne &&
                  requireAtLeastOneText &&
                  store.included?.length === 0
                    ? requireAtLeastOneText
                    : undefined
                }
              >
                <LoadingButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSubmit}
                  disabled={
                    (operationMode
                      ? !hasChanges && operationMode === OperationMode.EDIT
                      : !hasChanges) ||
                    (requireAtLeastOne && store.included?.length === 0)
                  }
                  children={saveButtonProps?.children ?? 'Save'}
                  {...saveButtonProps}
                />
              </Tooltip>
            </Box>
          </Box>
        }
      >
        <Styled.Backdrop open={!!loading}>
          <LoadingScreen />
        </Styled.Backdrop>

        <Grid2 columns={12} container>
          <TransferListExcluded
            title={excludedTitle}
            tooltipTitle={tooltipTitle}
          />
          <TransferListIncluded
            title={includedTitle}
            requireAtLeastOne={requireAtLeastOne}
            requireAtLeastOneText={requireAtLeastOneText}
          />
        </Grid2>
      </TransferListOpening>
    );
  }
);

TransferList.displayName = 'TransferList';
